import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { z } from "zod";
import { medusa } from "#/lib/medusa";
import { ROUTES } from "#/lib/routes";
import { selectCartId, selectHasHydrated, useCartStore } from "#/store/cart";

const returnSearchSchema = z.object({
  gateway: z.enum(["paymongo", "dragonpay"]).optional(),
  status: z.string().optional(),
  txnid: z.string().optional(),
  refno: z.string().optional(),
  message: z.string().optional(),
});

export const Route = createFileRoute("/order/return")({
  head: () => ({ meta: [{ title: "Confirming payment" }] }),
  validateSearch: returnSearchSchema,
  component: OrderReturnPage,
});

const COMPLETE_RETRIES = 3;
const COMPLETE_BACKOFF_MS = 1500;

const completeReturnKeys = {
  all: ["storefront", "order-return-complete"] as const,
  byCart: (cartId: string) =>
    ["storefront", "order-return-complete", cartId] as const,
};

function OrderReturnPage() {
  const search = Route.useSearch();
  const hasHydrated = useCartStore(selectHasHydrated);
  const cartId = useCartStore(selectCartId);
  const clearCart = useCartStore((s) => s.clearCart);
  const setLastOrderId = useCartStore((s) => s.setLastOrderId);

  const isDragonpayPending =
    search.gateway === "dragonpay" && search.status === "P";
  const isDragonpayFailed =
    search.gateway === "dragonpay" && search.status === "F";
  const isPaymongoFailed =
    search.gateway === "paymongo" && search.status === "failed";
  const isTerminalGatewayState =
    isDragonpayPending || isDragonpayFailed || isPaymongoFailed;

  const completeQuery = useQuery({
    queryKey: cartId
      ? completeReturnKeys.byCart(cartId)
      : completeReturnKeys.all,
    queryFn: async (): Promise<{ orderId: string }> => {
      if (!cartId) throw new Error("missing-cart");
      const result = await medusa.store.cart.complete(cartId);
      if (result.type === "order") {
        setLastOrderId(result.order.id);
        clearCart();
        return { orderId: result.order.id };
      }
      throw new Error(result.error.message);
    },
    enabled: hasHydrated && !!cartId && !isTerminalGatewayState,
    retry: COMPLETE_RETRIES - 1,
    retryDelay: COMPLETE_BACKOFF_MS,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  if (isDragonpayPending) {
    return (
      <Shell>
        <Status
          title="Payment pending"
          body={
            <>
              <p className="mt-4 font-display text-lp-text-lt text-sm">
                Your Dragonpay reference is{" "}
                <strong>{search.refno || "—"}</strong>. Pay within 48 hours at
                any 7-Eleven, Bayad Center, M Lhuillier, or partner outlet.
              </p>
              <p className="mt-2 font-display text-lp-text-lt text-sm">
                We'll email you once payment is confirmed.{" "}
                {search.message ?? ""}
              </p>
              <Link
                to={ROUTES.home}
                className="mt-6 inline-block rounded-full bg-lp-green-mid px-6 py-3 font-display font-semibold text-white"
              >
                Back to home
              </Link>
            </>
          }
        />
      </Shell>
    );
  }

  if (isDragonpayFailed || isPaymongoFailed) {
    return (
      <Shell>
        <FailedStatus message={search.message ?? "Payment failed"} />
      </Shell>
    );
  }

  if (hasHydrated && !cartId) {
    return (
      <Shell>
        <FailedStatus message="We can't find your cart in this browser. If you were charged, contact support with your reference." />
      </Shell>
    );
  }

  if (completeQuery.isSuccess) {
    return (
      <>
        <Navigate
          to="/order/$id"
          params={{ id: completeQuery.data.orderId }}
          search={{ paymongo_status: "success" }}
          replace
        />
        <Shell>
          <Status title="Payment confirmed" body="Redirecting to your order…" />
        </Shell>
      </>
    );
  }

  if (completeQuery.isError) {
    return (
      <Shell>
        <FailedStatus
          message={
            completeQuery.error instanceof Error
              ? completeQuery.error.message
              : "Could not confirm payment"
          }
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <Status
        title="Confirming your payment…"
        body="Hang tight — we're verifying with the payment gateway."
      />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-[640px] px-6 py-20 text-center">
      {children}
    </section>
  );
}

function FailedStatus({ message }: { message: string }) {
  return (
    <Status
      title="Payment not confirmed"
      body={
        <>
          <p className="mt-4 font-display text-lp-text-lt text-sm">{message}</p>
          <Link
            to={ROUTES.checkout}
            className="mt-6 inline-block rounded-full bg-lp-green-mid px-6 py-3 font-display font-semibold text-white"
          >
            Try again
          </Link>
        </>
      }
    />
  );
}

function Status({ title, body }: { title: string; body: React.ReactNode }) {
  return (
    <>
      <h1 className="font-bold font-display text-2xl text-lp-green-dk">
        {title}
      </h1>
      {typeof body === "string" ? (
        <p className="mt-4 font-display text-lp-text-lt text-sm">{body}</p>
      ) : (
        body
      )}
    </>
  );
}
